import React, { Component } from 'react';
import styled from 'styled-components';
import { get, keyBy as keyby, isEqual, map } from 'lodash';
import type { MyZonePageQuery } from '@graphql/pages/my-zone.generated';
import type { UserSessionData, RequestData } from '@lib/data-fetching';
import type { Locale } from '@lib/locale';
import {
  DATA_TRACK_TYPE_INTERNAL,
  MY_ZONE_OFFERS_PAGE,
  MY_ZONE_SHOPS_PAGE,
  OFFERS,
  SHOPS,
} from '../../../../lib/constants';
import { trackAnalyticsNew } from '../../../../lib/utils';
import MaxWidthLayout, { MaxWidthLayoutMobile } from '../../../templates/MaxWidthLayout';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import MyZoneContentWrapper from './MyZoneContentWrapper';
import MyZoneShopCard from './MyZoneShopCard';
import RenderLayoutViews from '../shared/RenderLayoutViews';
import TopBottomPadding from '../shared/TopBottomPadding';
import OfferCard from '../Offer/OfferCard';
import TabGroup from '../shared/TabGroup';
import type { PageInjectedProps } from '../Pages/Page';

const TABS = [MY_ZONE_SHOPS_PAGE, MY_ZONE_OFFERS_PAGE];
type ZoneData = NonNullable<NonNullable<MyZonePageQuery['allMyZone']>[number]>;
type Props = {
  myZonePageData: NonNullable<MyZonePageQuery['allMyZone']>;
  globalPageConfig: NonNullable<MyZonePageQuery['allGlobalPageConfigs']>[number] | Record<string, never>;
  locale: Locale;
  urlData: RequestData['urlData'];
  userData: UserSessionData['userData'];
  userShopsData: UserSessionData['userShopsData'];
  userOffersData: UserSessionData['userOffersData'];
  children?: React.ReactNode;
} & Partial<Pick<PageInjectedProps, 'pageStateModifiers'>>;
type State = { activeTab: string };
const TAB_ANALYTICS_NAMES = {
  [MY_ZONE_SHOPS_PAGE]: 'favourite-stores',
  [MY_ZONE_OFFERS_PAGE]: 'saved-offers',
};

const ScreenReaderOnlyH1 = styled.h1`
  position:absolute;
  left:-10000px;
  top:auto;
  width:1px;
  height:1px;
  overflow:hidden;
`;

class MyZonePage extends Component<Props, State> {
  myZoneData: Record<string, ZoneData>;

  initialShopsData: UserSessionData['userShopsData'];

  initialOffersData: UserSessionData['userOffersData'];

  constructor(props: Props) {
    super(props);
    const activeSlug = props.urlData?.query?.show;

    this.state = {
      activeTab: typeof activeSlug === 'string' && TABS.includes(activeSlug) ? activeSlug : MY_ZONE_SHOPS_PAGE,
    };

    const { myZonePageData, userShopsData, userOffersData } = props;

    // convert array of data into a dictionary where the keys are the slugs
    this.myZoneData = keyby(myZonePageData.filter((item): item is ZoneData => item !== null), 'slug');

    // slice() make a copy of the data
    // a copy is made because my zone needs to show the initial shop/offer
    //    cards even if the user unfavourites it while on the page
    this.initialShopsData = userShopsData.slice();
    this.initialOffersData = userOffersData.slice();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { userData, children } = this.props;
    const { state } = this;
    if (!isEqual(get(nextProps, 'userData'), userData)
        || !isEqual(state, nextState)) {
      return true;
    }
    return nextProps.children !== children;
  }

  setActiveTab = (activeSlug: string) => {
    const { activeTab } = this.state;
    if (activeSlug === activeTab) {
      return;
    }
    this.setState({ activeTab: activeSlug });
  };

  renderTabs(mobile: boolean) {
    const { myZoneData } = this;
    const { activeTab } = this.state;
    const tabs = TABS.flatMap((tab) => {
      const tabData = myZoneData[tab];
      if (!tabData) return [];
      return [{
        id: tabData.slug || '',
        label: tabData.displayName || '',
        analytics: {
          ...trackAnalyticsNew({
            location: 'page:header',
            name: TAB_ANALYTICS_NAMES[tab],
            url: undefined,
          }),
          dataTrackType: DATA_TRACK_TYPE_INTERNAL,
        },
      }];
    });
    const tabGroup = (
      <TabGroup
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={this.setActiveTab}
        isMobile={mobile}
        variant="myZone"
        ariaLabel="My Zone tabs"
        idPrefix={mobile ? 'my-zone-tabs-mobile' : 'my-zone-tabs'}
        focusActiveTabOnChange={mobile}
      />
    );
    return mobile ? <MaxWidthLayoutMobile>{tabGroup}</MaxWidthLayoutMobile> : tabGroup;
  }

  renderActivePage(activeTabData: ZoneData, mobile: boolean) {
    const { locale, userData, pageStateModifiers, globalPageConfig } = this.props;
    const { initialShopsData, initialOffersData } = this;
    const { activeTab } = this.state;
    if (activeTab === MY_ZONE_SHOPS_PAGE) {
      const favShopIds = map(Array.isArray(userData.shops) ? userData.shops : [], 'id') || [];
      return (
        <MyZoneContentWrapper
          globalPageConfig={globalPageConfig}
          itemsData={initialShopsData}
          favItemIds={favShopIds}
          itemType={SHOPS}
          CompToUse={MyZoneShopCard}
          pageStateModifiers={pageStateModifiers}
          locale={locale}
          tabData={activeTabData}
          userData={userData}
          isMobile={mobile}
        />
      );
    }
    if (activeTab === MY_ZONE_OFFERS_PAGE) {
      const favOffersIds = map(Array.isArray(userData.offers) ? userData.offers : [], 'id') || [];
      return (
        <MyZoneContentWrapper
          globalPageConfig={globalPageConfig}
          itemsData={initialOffersData}
          favItemIds={favOffersIds}
          itemType={OFFERS}
          CompToUse={OfferCard}
          pageStateModifiers={pageStateModifiers}
          locale={locale}
          tabData={activeTabData}
          userData={userData}
          isMobile={mobile}
        />
      );
    }
    return null;
  }

  render() {
    const { locale, userData, pageStateModifiers } = this.props;
    const { myZoneData } = this;
    const { activeTab } = this.state;
    const activeTabData = myZoneData[activeTab];
    if (!activeTabData) return null;

    const desktopPage = (
      <div>
        <ScreenReaderOnlyH1>My Zone</ScreenReaderOnlyH1>
        {this.renderTabs(false)}
        {this.renderActivePage(activeTabData, false)}
      </div>
    );
    const mobilePage = (
      <div>
        <ScreenReaderOnlyH1>My Zone</ScreenReaderOnlyH1>
        {this.renderTabs(true)}
        {this.renderActivePage(activeTabData, true)}
      </div>
    );

    return (
      <TopBottomPadding>
        <MaxWidthLayout>
          <MediaQueryHelper desktopChildren={desktopPage} mobileChildren={mobilePage} />
        </MaxWidthLayout>
        <RenderLayoutViews
          views={activeTabData.layout ? activeTabData.layout : []}
          locale={locale}
          userData={userData}
          pageStateModifiers={pageStateModifiers}
        />
      </TopBottomPadding>
    );
  }
}

export default MyZonePage;
